import { renderOfferingBadge, offeringLabel } from "./schedule/offering-badges";
import {
  isRegistrationClosed,
  scheduleRegistrationClosingRefresh,
} from "./registration-closing";
import { html, nothing, svg, type TemplateResult } from "lit";
import { timezoneGroups, tzAbbrev, type TimezoneGroup } from "./timezones";
import { customElement } from "lit/decorators.js";
import { createActorState, type ActorStateOf } from "../state/actor-state";
import { ZipperEmbedElement } from "./base/zipper-embed-element";
import { showAsTopLayer } from "../theme/theme";
import { scheduleStyles } from "./schedule/schedule.styles";
import type {
  ComponentType,
  EmbedConfig,
  ScheduleOptions,
} from "../config/types";
import {
  addDays,
  makeMockSchedule,
  sameDay,
  type MockClass,
} from "./data/mock-schedule";
import { type PricingTier } from "./data/mock-pricing";
import { scrollToStart } from "./internal/composed-layout";
import {
  fetchSchedule,
  fetchEventDescription,
  fetchMemberContext,
  type ScheduleDateRange,
} from "../data/live";
import {
  capacityBadge,
  classColorStyle,
  initialAnchorOccurrence,
  isPastOccurrence,
  isNotYetOpen,
  occurrenceInstant,
  startOfDay,
  startOfZonedDay,
} from "./schedule-helpers";
import { renderScheduleRow } from "./schedule/schedule-row.view";
import { openSignIn } from "./internal/open-signin";
import { clearEmbedToken } from "../config/client";
import "./internal/zipper-booking-checkout";
import "./internal/zipper-commerce-checkout";
import {
  CommerceOfferingKind,
  CommerceRequestedAction,
  type CommerceClassOfferingRefDto,
} from "@zipper/customer-commerce-client";
import "./internal/zipper-filter-pill";
import { formatMinorAmount } from "./format-money";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export interface ScheduleMonthRange extends ScheduleDateRange {
  key: string;
}

/**
 * API range covering a calendar's six-week grid. A one-day guard on either
 * side prevents a UTC/local offset at the API boundary from hiding an overflow
 * day while still keeping the request tightly month-scoped.
 */
export function scheduleMonthRange(month: Date): ScheduleMonthRange {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(Date.UTC(year, monthIndex, 1));
  const gridStart = new Date(first);
  gridStart.setUTCDate(gridStart.getUTCDate() - first.getUTCDay() - 1);
  const gridEnd = new Date(gridStart);
  gridEnd.setUTCDate(gridEnd.getUTCDate() + 43);
  return {
    key: `${year}-${String(monthIndex + 1).padStart(2, "0")}`,
    minDate: gridStart.toISOString(),
    maxDate: gridEnd.toISOString(),
  };
}

/**
 * PLAT-6790: list/week load in windows instead of one 120-day fetch. Orgs with
 * hundreds of sessions a day made that single fetch take up to a minute
 * before anything rendered. The first window paints; later windows load as
 * the visitor scrolls or navigates, up to the horizon.
 */
export const SCHEDULE_LOOKBACK_DAYS = 7;
export const SCHEDULE_WINDOW_DAYS = 14;
export const SCHEDULE_HORIZON_DAYS = 180;

/** API range for `days` days starting at local midnight of `from`. */
export function scheduleWindowRange(
  from: Date,
  days: number,
): { minDate: string; maxDate: string } {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return { minDate: start.toISOString(), maxDate: end.toISOString() };
}

// 'month' renders the labelled calendar grid (surfaced to visitors as
// "Calendar"); 'week' is the 7-day column layout. 'day' and 'list' share the
// vertical agenda, showing one selected date or an extending list respectively.
type ViewMode = "list" | "day" | "list-week" | "month" | "week";

// Shared inline icons (handoff SVGs).
const ICON_SIGNIN = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>`;
const ICON_CHEVRON_L = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
const ICON_CHEVRON_R = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;
const ICON_CARET = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
// Mobile: collapse the filter pills into one "Filter" affordance.
const ICON_FILTER = svg`<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="10" y1="18" x2="14" y2="18"/></svg>`;
// N2: timezone picker affordance.
const ICON_CLOCK = svg`<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg>`;

/**
 * Format a class's start instant as a wall-clock time (e.g. "9:30 AM") in the
 * given IANA `tz`. Falls back to `fallback` when there's no instant to project
 * or the zone is unusable (an invalid IANA name makes Intl throw). Exported so
 * the zone re-projection is unit-testable independent of the Lit component
 * (see zipper-schedule.test.ts) — the N2 picker drives `tz` here.
 */
export function formatZonedTime(
  startIso: string | undefined,
  tz: string | undefined,
  fallback: string,
): string {
  if (!startIso) return fallback;
  try {
    return new Date(startIso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz || undefined,
    });
  } catch {
    return fallback;
  }
}

/**
 * Earliest date the schedule LIST view should render occurrences from.
 *
 * On first paint — `activeDate` is still wherever it was initialized to
 * ('start', i.e. today or the org's configured 'startDate') — this is just
 * `start` itself, so the first list section header lines up with the
 * highlighted day in the week strip (A1). It only drifts earlier than
 * `start` once the visitor has actually navigated the strip back to a prior
 * day (`activeDate < start`, e.g. via the week strip's back arrow, which
 * unlike the month-picker isn't blocked at 'start') — and even then it's
 * clamped to `lookbackDays` (default 30) so the list never has to render
 * unbounded history. That lookback floor is the MORE PERMISSIVE (earlier) of
 * "30 days before today" and `start`, so a visitor can always page back at
 * least a month even when `start` is today, and can still reach an
 * even-earlier configured `start` without the flat 30-day cap cutting it off.
 *
 * Exported so the anchor-date math is unit-testable independent of the Lit
 * component (see zipper-schedule.test.ts). Previously the "haven't navigated
 * yet" case wasn't handled at all — the function unconditionally returned
 * the permissive lookback floor unless `activeDate` had *already* been
 * pushed further back than it (never true on first paint), so the list
 * opened a month behind the strip on every load instead of at `start`.
 */
export function computeListStartDate(
  today: Date,
  start: Date,
  activeDate: Date,
  lookbackDays = 30,
): Date {
  if (activeDate >= start) return startOfDay(start);
  const lookbackFloor = addDays(today, -lookbackDays);
  const floor = lookbackFloor < start ? lookbackFloor : start;
  return startOfDay(activeDate < floor ? floor : activeDate);
}

type ScheduleLoadFailure =
  { kind: "initial" } | { kind: "more" } | { kind: "month"; month: Date };

const scheduleActorState = createActorState(() => ({
  view: "list" as ViewMode,
  selectedTz: "",
  classFilter: "",
  instructorFilter: "",
  locationFilter: "",
  activeDate: startOfDay(new Date()),
  selectedMonthDay: null as Date | null,
  monthCursor: null as Date | null,
  weekAnchor: null as Date | null,
  monthPickerOpen: false,
  pickerMonth: null as number | null,
  filterSheetOpen: false,
  pickerYear: 0,
  bookingClass: null as MockClass | null,
  bookingAction: CommerceRequestedAction.Book as CommerceRequestedAction,
  classPopover: null as {
    cls: MockClass;
    top: number;
    left: number;
  } | null,
  signedInName: "",
  expandedRowIds: new Set<string>(),
  expandedBioIds: new Set<string>(),
  descriptionCache: {} as Record<string, string>,
  descriptionLoadingIds: new Set<string>(),
  schedule: [] as MockClass[],
  scheduleLoading: true,
  scheduleError: null as ScheduleLoadFailure | null,
  /** PLAT-6790: end (exclusive) of the contiguous window loaded for list/week. */
  loadedUntil: null as Date | null,
  loadingMore: false,
  nextDayStatus: "idle" as "idle" | "searching" | "exhausted" | "error",
  nowInstant: new Date(),
}));

type ScheduleActorState = ActorStateOf<typeof scheduleActorState>;

/**
 * <zipper-schedule instance="..."> — class/booking schedule.
 *
 * Ported from the design-handoff `schedule.jsx` ("Book a Class"). Renders from
 * mock data until the live org/location data source is wired in, but honors
 * every ScheduleOptions field so the admin config editor can be tested.
 */
@customElement("zipper-schedule")
export class ZipperSchedule extends ZipperEmbedElement {
  static styles = scheduleStyles;

  @scheduleActorState.field("view")
  private view!: ScheduleActorState["view"];

  /** N2: the IANA time zone the visitor picked from the zone dropdown; every
   * class time renders in it. Empty = no explicit pick yet, so times default to
   * each class's own studio zone and the dropdown preselects the location zone
   * (see `displayTime` / `effectiveTz` / `renderTzSwitch`). */
  @scheduleActorState.field("selectedTz")
  private selectedTz!: ScheduleActorState["selectedTz"];

  @scheduleActorState.field("classFilter")
  private classFilter!: ScheduleActorState["classFilter"];

  @scheduleActorState.field("instructorFilter")
  private instructorFilter!: ScheduleActorState["instructorFilter"];

  @scheduleActorState.field("locationFilter")
  private locationFilter!: ScheduleActorState["locationFilter"];

  @scheduleActorState.field("activeDate")
  private activeDate!: ScheduleActorState["activeDate"];

  @scheduleActorState.field("selectedMonthDay")
  private selectedMonthDay!: ScheduleActorState["selectedMonthDay"];

  /** Month being shown in the calendar view (1st of the month). */
  @scheduleActorState.field("monthCursor")
  private monthCursor!: ScheduleActorState["monthCursor"];

  /** Week shown in the date strip; null = follow "today". */
  @scheduleActorState.field("weekAnchor")
  private weekAnchor!: ScheduleActorState["weekAnchor"];

  @scheduleActorState.field("monthPickerOpen")
  private monthPickerOpen!: ScheduleActorState["monthPickerOpen"];

  /** Month index (0–11) when the picker is on the day-selection step; null = month grid. */
  @scheduleActorState.field("pickerMonth")
  private pickerMonth!: ScheduleActorState["pickerMonth"];

  /** Mobile filter sheet (MOB-26) open state. */
  @scheduleActorState.field("filterSheetOpen")
  private filterSheetOpen!: ScheduleActorState["filterSheetOpen"];

  @scheduleActorState.field("pickerYear")
  private pickerYear!: ScheduleActorState["pickerYear"];

  @scheduleActorState.field("bookingClass")
  private bookingClass!: ScheduleActorState["bookingClass"];

  @scheduleActorState.field("bookingAction")
  private bookingAction!: ScheduleActorState["bookingAction"];

  /** Calendar month-view class-detail popover (anchored to a clicked bar).
   * NB: not named `popover` — that clashes with the native HTMLElement.popover. */
  @scheduleActorState.field("classPopover")
  private classPopover!: ScheduleActorState["classPopover"];

  @scheduleActorState.field("signedInName")
  private signedInName!: ScheduleActorState["signedInName"];

  /** Ids of rows whose description is expanded (chevron clicked). */
  @scheduleActorState.field("expandedRowIds")
  private expandedRowIds!: ScheduleActorState["expandedRowIds"];

  /** SCH-08: ids of rows whose instructor bio is expanded (instructor name
   * clicked). Separate from `expandedRowIds` so the title/description and
   * instructor-bio accordions toggle independently on the same row. */
  @scheduleActorState.field("expandedBioIds")
  private expandedBioIds!: ScheduleActorState["expandedBioIds"];

  /** Descriptions fetched on demand when a row expands, keyed by class id
   * (mock rows skip this — their description is already inlined). */
  @scheduleActorState.field("descriptionCache")
  private descriptionCache!: ScheduleActorState["descriptionCache"];

  @scheduleActorState.field("descriptionLoadingIds")
  private descriptionLoadingIds!: ScheduleActorState["descriptionLoadingIds"];

  @scheduleActorState.field("schedule")
  private schedule!: ScheduleActorState["schedule"];

  /** True until the org's real classes (or the mock fallback) have loaded —
   * keeps the skeleton up instead of flashing "No upcoming classes." while
   * the schedule fetch is still in flight (can take up to ~1 min). */
  @scheduleActorState.field("scheduleLoading")
  private scheduleLoading!: ScheduleActorState["scheduleLoading"];

  @scheduleActorState.field("scheduleError")
  private scheduleError!: ScheduleActorState["scheduleError"];

  @scheduleActorState.field("loadedUntil")
  private loadedUntil!: ScheduleActorState["loadedUntil"];

  @scheduleActorState.field("loadingMore")
  private loadingMore!: ScheduleActorState["loadingMore"];

  @scheduleActorState.field("nextDayStatus")
  private nextDayStatus!: ScheduleActorState["nextDayStatus"];
  private nextDayGeneration = 0;

  /** In-flight forward window load, shared so callers don't double-fetch. */
  private moreInFlight: Promise<void> | null = null;
  /** Watches the end of the list so the next window loads before it's reached. */
  private listEndObserver: IntersectionObserver | null = null;
  /** Defers the initial next-class reveal until the embed is on screen. */
  private initialScrollObserver: IntersectionObserver | null = null;
  private initialScrollInViewport = false;

  private cancelClosingRefresh?: () => void;

  private tiersByClassId: Record<string, PricingTier[]> = {};
  /**
   * Reference "now" as a real INSTANT. Only ever compared against other
   * instants ('isPastOccurrence'), never used for day math — see 'today'.
   * Re-stamped when the schedule loads so a tab left open overnight doesn't
   * anchor on a stale day.
   */
  @scheduleActorState.field("nowInstant")
  private nowInstant!: ScheduleActorState["nowInstant"];
  /**
   * Reference "today" as a calendar DAY, resolved in the zone the visitor is
   * reading times in ('effectiveTz') rather than the device's raw local date.
   * A getter, not a stored field, so picking a different zone from the switcher
   * re-derives it — and so it can never drift out of sync with 'nowInstant'.
   */
  private get today(): Date {
    return startOfZonedDay(this.nowInstant, this.effectiveTz);
  }
  private loadedAuthorityKey = "";
  /** Exact row to reveal after the first list render for this authority. The
   * ID is consumed before scrolling so subsequent reactive renders cannot
   * repeat the automatic movement. */
  private pendingInitialScrollId: string | null = null;
  /**
   * True once the visitor has actually scrolled. Until then the day-section
   * IntersectionObserver is muted, so the deterministic first-paint anchor
   * (see 'anchorInitialDate') can't be immediately overwritten by whichever
   * section happens to sit in the observer's band at load.
   */
  private userScrolled = false;
  private navigationGeneration = 0;
  private explicitDateNavigation = false;
  private observedScrollRoot: Element | null = null;
  private observedRootMargin = "";
  private stickyResizeObserver: ResizeObserver | null = null;
  private stickyResizeTarget: Element | null = null;

  /** Scroll events are also emitted by scrollIntoView; only user intent releases selection. */
  private readonly onUserScrollIntent = (event: Event): void => {
    if (!this.isConnected || !this.tracksScrollSelection || this.bookingClass) return;
    const root = this.findScrollRoot();
    const target = event.target;
    if (
      root &&
      !event.composedPath().includes(this) &&
      !(target instanceof Node && root.contains(target))
    )
      return;
    if (event instanceof KeyboardEvent) {
      if (
        ![
          "ArrowUp",
          "ArrowDown",
          "PageUp",
          "PageDown",
          "Home",
          "End",
          " ",
        ].includes(event.key)
      )
        return;
      if (
        event
          .composedPath()
          .some(
            (node) =>
              node instanceof HTMLElement &&
              (node.isContentEditable ||
                ["INPUT", "TEXTAREA", "SELECT"].includes(node.tagName) ||
                (event.key === " " && node.tagName === "BUTTON")),
          )
      )
        return;
    }
    this.userScrolled = true;
    this.explicitDateNavigation = false;
    this.navigationGeneration++;
    this.observeDaySections();
  };

  private readonly onScrollLayoutChange = (): void => {
    if (!this.isConnected || !this.isListView || this.bookingClass) return;
    this.syncStickyOffset();
    this.observeDaySections();
  };

  private beginDateNavigation(): number {
    this.userScrolled = false;
    this.explicitDateNavigation = true;
    this.pendingInitialScrollId = null;
    this.teardownInitialScrollObserver();
    return ++this.navigationGeneration;
  }

  /** Follow parent elements and shadow hosts to the actual vertical scroller. */
  private findScrollRoot(): Element | null {
    const ownStyle = getComputedStyle(this);
    if (
      /(auto|scroll|overlay)/.test(ownStyle.overflowY || ownStyle.overflow) &&
      this.scrollHeight > this.clientHeight
    ) return this;
    let element: Element | null =
      this.parentElement ??
      (this.getRootNode() instanceof ShadowRoot
        ? (this.getRootNode() as ShadowRoot).host
        : null);
    while (
      element &&
      element !== document.body &&
      element !== document.documentElement
    ) {
      const style = getComputedStyle(element);
      if (
        /(auto|scroll|overlay)/.test(style.overflowY || style.overflow) &&
        element.scrollHeight > element.clientHeight
      )
        return element;
      const parent: Element | null = element.parentElement;
      const tree = element.getRootNode();
      element = parent ?? (tree instanceof ShadowRoot ? tree.host : null);
    }
    return null;
  }

  /** Drives the scroll-linked day-pill highlight in list view (bug fix: the
   * date strip previously never advanced as the list was scrolled). */
  private daySectionObserver: IntersectionObserver | null = null;
  /** Day sections currently intersecting the active band, tracked across
   * observer callbacks so the topmost visible day is chosen from the FULL set
   * (not just the entries that changed in one callback — which left the pill a
   * day behind while scrolling). */
  private intersectingSections = new Set<Element>();
  /** `data-offset` keys of the sections the live observer is watching, so it's
   * reused (not torn down mid-scroll) while the rendered days are unchanged. */
  private observedSectionKeys = "";
  /** Successful/in-flight month requests for the current embed authority. */
  private loadedMonthKeys = new Set<string>();

  protected get expectedType(): ComponentType {
    return "schedule";
  }

  private get opts(): ScheduleOptions {
    return (this.config?.options as ScheduleOptions) ?? {};
  }

  private get isSingleDayList(): boolean {
    return this.view === "day";
  }

  private get isWeekList(): boolean {
    return this.view === "list-week";
  }

  private get isBoundedList(): boolean {
    return this.isSingleDayList || this.isWeekList;
  }

  private listWeekStart(): Date {
    return startOfDay(this.weekAnchor ?? this.today);
  }

  private get isListView(): boolean {
    return this.view === "list" || this.isBoundedList;
  }

  private get tracksScrollSelection(): boolean {
    return this.view === "list" || this.isWeekList;
  }

  private nextDayContext(): string {
    return JSON.stringify([this.loadedAuthorityKey, this.view, this.activeDate.getTime(), this.weekAnchor?.getTime(),
      this.effectiveTz, this.classFilter, this.instructorFilter, this.locationFilter]);
  }

  /** Use the contiguous list window, even when calendar navigation cached later days. */
  private async findNextPeriod(): Promise<void> {
    if (!this.isBoundedList || this.nextDayStatus === "searching") return;
    const generation = ++this.nextDayGeneration;
    const context = this.nextDayContext();
    const current = () => this.isConnected && generation === this.nextDayGeneration && context === this.nextDayContext();
    const weekStart = this.listWeekStart();
    const selected = this.isWeekList
      ? addDays(weekStart, 6).getTime() : startOfDay(this.activeDate).getTime();
    const horizon = addDays(startOfDay(new Date()), SCHEDULE_HORIZON_DAYS).getTime();
    this.nextDayStatus = "searching";
    while (current()) {
      const next = this.filtered().map((c) => this.dayOf(c))
        .filter((day) => day.getTime() > selected && day.getTime() < horizon)
        .sort((a, b) => a.getTime() - b.getTime())[0];
      // One extra day covers device/display-zone offsets at a window boundary.
      const covered = next && (!this.loadedUntil || this.loadedUntil.getTime() >= addDays(next, 2).getTime() || !this.canLoadMore());
      if (covered) {
        this.nextDayStatus = "idle";
        if (this.isWeekList) {
          const daysAhead = Math.round((next.getTime() - weekStart.getTime()) / 86400000);
          this.weekAnchor = addDays(weekStart, Math.floor(daysAhead / 7) * 7);
        }
        this.jumpToDay(next);
        return;
      }
      if (this.scheduleError) {
        this.nextDayStatus = "error";
        return;
      }
      if (!this.canLoadMore()) {
        this.nextDayStatus = "exhausted";
        return;
      }
      await this.loadMore();
    }
  }

  // Apply the configured default view + load live data once config arrives.
  protected willUpdate(changed: Map<string, unknown>): void {
    if ((changed.has("config") || changed.has("runtime")) && this.config) {
      const dv = this.opts.defaultView;
      const views = this.enabledViews();
      this.view = views.find((v) => v.id === dv)?.id
        ?? views.find((v) => v.id === "list")?.id
        ?? views[0]?.id ?? "month";
      const authorityKey = this.discoveryAuthorityKey();
      if (authorityKey !== this.loadedAuthorityKey) {
        this.loadedAuthorityKey = authorityKey;
        this.loadedMonthKeys.clear();
        this.pendingInitialScrollId = null;
        this.initialScrollInViewport = false;
        this.teardownInitialScrollObserver();
        this.userScrolled = false;
        this.explicitDateNavigation = false;
        this.navigationGeneration++;
        this.bookingClass = null;
        this.classPopover = null;
        this.schedule = [];
        this.tiersByClassId = {};
        this.loadedUntil = null;
        this.loadingMore = false;
        this.moreInFlight = null;
        this.scheduleLoading = true;
        this.scheduleError = null;
        this.signedInName = "";
        void this.loadSchedule(authorityKey);
        void this.loadMember(authorityKey);
      }
    }
  }

  private discoveryAuthorityKey(): string {
    return JSON.stringify({
      orgId: this.config?.orgId ?? 0,
      locationId: this.config?.locationId ?? null,
      options: this.config?.options ?? {},
      viewerRevision: this.runtime.viewerRevision ?? "guest",
    });
  }

  /**
   * Load the org's real classes.
   *
   * Mock data is ONLY for an embed with no org configured at all (the local
   * demo harness) — matching 'zipper-events'' 'loadEvents'. A configured org
   * whose window comes back empty, or whose fetch fails, renders the genuine
   * empty state.
   *
   * This used to fall back to 'makeMockSchedule()' on empty OR error, which
   * meant a real studio with no classes in the next 90 days published a page
   * of invented classes ("Reformer Flow", "Marisol Pena", "Allendale") dated
   * around the mock's hardcoded May 2026 anchor. Visitors could click Book on
   * sessions that do not exist, and the studio had no way to tell the schedule
   * had failed. An empty schedule is a legitimate state; fabricated inventory
   * on someone's live site is not.
   */
  private async loadSchedule(authorityKey: string): Promise<void> {
    const cfg = this.config;
    if (!cfg) {
      if (authorityKey === this.loadedAuthorityKey) {
        this.scheduleLoading = false;
      }
      return;
    }
    if (!cfg.orgId) {
      if (authorityKey !== this.loadedAuthorityKey) return;
      this.nowInstant = new Date();
      this.schedule = makeMockSchedule(this.today);
      this.anchorInitialDate();
      this.scheduleLoading = false;
      return;
    }
    try {
      const from = addDays(new Date(), -SCHEDULE_LOOKBACK_DAYS);
      const days = SCHEDULE_LOOKBACK_DAYS + SCHEDULE_WINDOW_DAYS;
      const { classes, tiersByClassId } = await fetchSchedule(
        cfg.orgId,
        cfg.locationId ?? undefined,
        scheduleWindowRange(from, days),
        this.opts.includeOneOffEvents !== false,
      );
      if (authorityKey !== this.loadedAuthorityKey) return;
      this.schedule = classes;
      this.tiersByClassId = tiersByClassId;
      this.scheduleError = null;
      this.loadedUntil = addDays(startOfDay(from), days);
      // First paint anchors on the next upcoming class; a quiet studio may
      // have none in the first window, so keep reaching forward (bounded).
      this.nowInstant = new Date();
      while (
        !initialAnchorOccurrence(
          this.filtered(),
          this.nowInstant,
          this.startDate(),
          this.effectiveTz,
        ) &&
        this.canLoadMore() &&
        !this.scheduleError
      ) {
        await this.loadMore();
        if (authorityKey !== this.loadedAuthorityKey) return;
      }
    } catch {
      if (authorityKey !== this.loadedAuthorityKey) return;
      // A failed request is not an empty or complete successful schedule.
      this.schedule = [];
      this.tiersByClassId = {};
      this.scheduleError = { kind: "initial" };
    } finally {
      if (authorityKey === this.loadedAuthorityKey) {
        this.nowInstant = new Date();
        this.anchorInitialDate();
        this.scheduleLoading = false;
      }
    }
  }

  /** Load exactly the calendar grid being viewed and merge it into the initial
   * schedule. Overlap is replaced by ID so canceled/deleted rows do not linger,
   * while sessions loaded for other months remain available when navigating
   * back. Failed ranges are uncached and therefore retryable. */
  private async loadCalendarMonth(month: Date): Promise<void> {
    const cfg = this.config;
    if (!cfg?.orgId) return;
    const authorityKey = this.loadedAuthorityKey;
    const range = scheduleMonthRange(month);
    const requestKey = `${authorityKey}:${range.key}`;
    if (this.loadedMonthKeys.has(requestKey)) return;
    this.loadedMonthKeys.add(requestKey);

    try {
      const { classes, tiersByClassId } = await fetchSchedule(
        cfg.orgId,
        cfg.locationId ?? undefined,
        range,
        this.opts.includeOneOffEvents !== false,
      );
      if (authorityKey !== this.loadedAuthorityKey) return;
      this.mergeRange(range, classes, tiersByClassId);
      this.scheduleError = null;
    } catch {
      if (authorityKey === this.loadedAuthorityKey) {
        this.loadedMonthKeys.delete(requestKey);
        this.scheduleError = { kind: "month", month };
      }
    }
  }

  /** Replace the sessions inside `range` with a fresh load, keeping the rest. */
  private mergeRange(
    range: { minDate: string; maxDate: string },
    classes: MockClass[],
    tiersByClassId: Record<string, PricingTier[]>,
  ): void {
    const min = new Date(range.minDate).getTime();
    const max = new Date(range.maxDate).getTime();
    const merged = new Map<string, MockClass>();
    for (const cls of this.schedule) {
      const instant = new Date(cls.startIso || cls.date).getTime();
      if (instant < min || instant > max) merged.set(cls.id, cls);
    }
    for (const cls of classes) merged.set(cls.id, cls);
    this.schedule = [...merged.values()].sort(
      (a, b) =>
        new Date(a.startIso || a.date).getTime() -
        new Date(b.startIso || b.date).getTime(),
    );
    const nextTiers = { ...this.tiersByClassId, ...tiersByClassId };
    const retainedIds = new Set(this.schedule.map((cls) => cls.id));
    this.tiersByClassId = Object.fromEntries(
      Object.entries(nextTiers).filter(([id]) => retainedIds.has(id)),
    );
  }

  /** PLAT-6790: more of the list/week window exists before the horizon. */
  private canLoadMore(): boolean {
    if (!this.config?.orgId || !this.loadedUntil) return false;
    return (
      this.loadedUntil.getTime() <
      addDays(startOfDay(new Date()), SCHEDULE_HORIZON_DAYS).getTime()
    );
  }

  /**
   * PLAT-6790: load the next window after `loadedUntil` and merge it. Shared
   * while in flight; a failure leaves `loadedUntil` alone so it is retried.
   */
  private loadMore(): Promise<void> {
    if (this.moreInFlight) return this.moreInFlight;
    const cfg = this.config;
    const from = this.loadedUntil;
    if (!cfg?.orgId || !from || !this.canLoadMore() || this.scheduleError)
      return Promise.resolve();
    const authorityKey = this.loadedAuthorityKey;
    const range = scheduleWindowRange(from, SCHEDULE_WINDOW_DAYS);
    this.loadingMore = true;
    this.moreInFlight = (async () => {
      try {
        const { classes, tiersByClassId } = await fetchSchedule(
          cfg.orgId,
          cfg.locationId ?? undefined,
          range,
          this.opts.includeOneOffEvents !== false,
        );
        if (authorityKey !== this.loadedAuthorityKey) return;
        this.mergeRange(range, classes, tiersByClassId);
        this.loadedUntil = addDays(from, SCHEDULE_WINDOW_DAYS);
        this.scheduleError = null;
      } catch {
        if (authorityKey === this.loadedAuthorityKey) {
          this.scheduleError = { kind: "more" };
        }
        // Keep the failed window identity; only an explicit Retry resumes it.
      } finally {
        if (authorityKey === this.loadedAuthorityKey) {
          this.loadingMore = false;
          this.moreInFlight = null;
        }
      }
    })();
    return this.moreInFlight;
  }

  /** PLAT-6790: make sure every day up to and including `day` is loaded. */
  private async ensureLoadedThrough(day: Date): Promise<void> {
    const target = addDays(startOfDay(day), 1).getTime();
    while (
      this.loadedUntil &&
      this.loadedUntil.getTime() < target &&
      this.canLoadMore()
    ) {
      const before = this.loadedUntil.getTime();
      await this.loadMore();
      if (!this.loadedUntil || this.loadedUntil.getTime() === before) return;
    }
  }

  /**
   * First-paint anchor: "default to today, and the next event."
   *
   * Derived from the loaded data via {@link initialAnchorOccurrence} — today when
   * today still has a class to come, otherwise the next day that does — so it
   * is the same on every load rather than depending on where the host page
   * happened to be scrolled. In list view, the target ID is retained just long
   * enough for `updated()` to reveal the exact actionable row once it exists.
   */
  private anchorInitialDate(): void {
    const start = this.startDate();
    const target = initialAnchorOccurrence(
      this.filtered(),
      this.nowInstant,
      start,
      this.effectiveTz,
    );
    this.weekAnchor = null;
    this.activeDate = target
      ? startOfZonedDay(occurrenceInstant(target), this.effectiveTz)
      : startOfDay(start);
    this.pendingInitialScrollId =
      this.isListView ? (target?.id ?? null) : null;
    // Keep the 7-day strip window over the anchor (a no-op while it's inside
    // today's week, which is the common case).
    this.moveWeekAnchorFor(this.activeDate);
  }

  /** Restore signed-in greeting when a JWT is already in localStorage. */
  private async loadMember(authorityKey: string): Promise<void> {
    const dashboardName = this.runtime.customer?.displayName?.trim();
    if (dashboardName) {
      this.signedInName = dashboardName.split(/\s+/)[0] || dashboardName;
      return;
    }
    const orgId = this.config?.orgId;
    if (!orgId) return;
    const ctx = await fetchMemberContext(orgId);
    if (authorityKey !== this.loadedAuthorityKey) return;
    if (ctx?.firstName) this.signedInName = ctx.firstName;
  }

  private logout(): void {
    if (this.config?.orgId) clearEmbedToken(this.config.orgId);
    this.signedInName = "";
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("resize", this.onScrollLayoutChange);
    for (const type of ["wheel", "touchmove", "keydown"]) {
      window.addEventListener(type, this.onUserScrollIntent, {
        capture: true,
        passive: true,
      });
    }
  }

  updated(changed: Map<string, unknown>): void {
    super.updated(changed);
    // Options are rendered asynchronously by Lit; select only after they exist.
    const viewSelect = this.renderRoot.querySelector<HTMLSelectElement>(".view-select");
    if (viewSelect) viewSelect.value = this.view;
    if (["activeDate", "weekAnchor", "selectedTz", "classFilter", "instructorFilter", "locationFilter", "view", "config", "runtime"].some((key) => changed.has(key))) {
      this.nextDayGeneration++;
      this.nextDayStatus = "idle";
    }
    if (
      [
        "view",
        "bookingClass",
        "selectedTz",
        "classFilter",
        "instructorFilter",
        "locationFilter",
      ].some((key) => changed.has(key))
    ) {
      this.navigationGeneration++;
      this.explicitDateNavigation = false;
      this.userScrolled = false;
    }
    this.cancelClosingRefresh?.();
    if (this.isConnected) {
      this.cancelClosingRefresh = scheduleRegistrationClosingRefresh(
        this.schedule,
        () => {
          this.nowInstant = new Date();
        },
      );
    }
    if (this.isListView && !this.bookingClass) {
      this.syncStickyOffset();
      this.observeDaySections();
      this.observeInitialScrollViewport();
      this.scrollToInitialClass();
      this.observeListEnd();
    } else {
      this.teardownDayObserver();
      this.teardownListEndObserver();
    }
    // PLAT-6790: the week view needs its whole week loaded.
    if (
      (this.view === "week" || this.isWeekList) &&
      (changed.has("weekAnchor") ||
        changed.has("view") ||
        changed.has("loadedUntil"))
    ) {
      void this.ensureLoadedThrough(
        addDays(this.isWeekList ? this.listWeekStart() : this.weekAnchor ?? this.startDate(), 6),
      );
    }
    // SC6: lift the month picker / filter sheet into the browser top layer so
    // their fixed backdrops center in the visible viewport instead of on the
    // (potentially very tall) embed's scroll height.
    if (this.monthPickerOpen) {
      showAsTopLayer(this.renderRoot.querySelector(".mp-overlay"));
    }
    if (this.filterSheetOpen) {
      showAsTopLayer(this.renderRoot.querySelector(".fs-overlay"));
    }
  }

  /** Reveal the next actionable class once, only after its list row exists. */
  private scrollToInitialClass(): void {
    const id = this.pendingInitialScrollId;
    if (
      !id ||
      !this.initialScrollInViewport ||
      this.scheduleLoading ||
      !this.isListView
    ) {
      return;
    }
    const target = Array.from(
      this.renderRoot.querySelectorAll(".row[data-occurrence-id]"),
    ).find((row) => row.getAttribute("data-occurrence-id") === id);
    if (!target) return;
    this.pendingInitialScrollId = null;
    // scrollIntoView also scrolls the website's ancestors. Initial positioning
    // may move only this component's own list/host, never the enclosing page.
    for (
      let scroller = target.parentElement;
      scroller;
      scroller = scroller === this ? null : (scroller.parentElement ?? this)
    ) {
      const overflow = getComputedStyle(scroller).overflowY;
      if (
        !/^(auto|scroll)$/.test(overflow) ||
        scroller.scrollHeight <= scroller.clientHeight
      ) {
        continue;
      }
      // Visibility can arrive after the controls have wrapped or finished
      // loading. Refresh their measured clearance at the actual scroll time.
      this.syncStickyOffset();
      const clearance =
        parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      scroller.scrollTop = Math.max(
        0,
        scroller.scrollTop +
          target.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top -
          scroller.clientTop -
          clearance,
      );
      return;
    }
    // A growing embed has no owned scroller. Consume the one-time anchor
    // without moving its website, including a visitor's fragment arrival.
  }

  /**
   * An embed can finish loading while it is well below the visitor's current
   * position. Do not let its initial next-class anchor move the host page until
   * the visitor has actually reached this component. Explicit user actions use
   * their own scroll paths and are intentionally not gated here.
   */
  private observeInitialScrollViewport(): void {
    if (
      this.initialScrollInViewport ||
      this.initialScrollObserver ||
      !this.pendingInitialScrollId ||
      this.scheduleLoading ||
      !this.isListView ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }
    this.initialScrollObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.initialScrollInViewport = true;
      this.teardownInitialScrollObserver();
      this.scrollToInitialClass();
    });
    this.initialScrollObserver.observe(this);
  }

  private teardownInitialScrollObserver(): void {
    this.initialScrollObserver?.disconnect();
    this.initialScrollObserver = null;
  }

  /**
   * PLAT-6790: load the next window when the end of the list comes within a
   * screen of the viewport. Re-armed after each render, since the marker is
   * re-created as days are appended.
   */
  private observeListEnd(): void {
    const marker = this.renderRoot.querySelector(".list-more");
    this.teardownListEndObserver();
    if (
      this.isBoundedList ||
      !marker ||
      this.scheduleError ||
      this.scheduleLoading ||
      this.loadingMore ||
      typeof IntersectionObserver === "undefined"
    )
      return;
    this.listEndObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void this.loadMore();
      },
      { root: this.findScrollRoot(), rootMargin: "0px 0px 100% 0px" },
    );
    this.listEndObserver.observe(marker);
  }

  private teardownListEndObserver(): void {
    this.listEndObserver?.disconnect();
    this.listEndObserver = null;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cancelClosingRefresh?.();
    this.cancelClosingRefresh = undefined;
    this.teardownDayObserver();
    this.teardownListEndObserver();
    this.teardownInitialScrollObserver();
    this.navigationGeneration++;
    this.nextDayGeneration++;
    window.removeEventListener("resize", this.onScrollLayoutChange);
    this.stickyResizeObserver?.disconnect();
    this.stickyResizeObserver = null;
    this.stickyResizeTarget = null;
    for (const type of ["wheel", "touchmove", "keydown"])
      window.removeEventListener(type, this.onUserScrollIntent, {
        capture: true,
      });
  }

  private filtered(): MockClass[] {
    const o = this.opts;
    // ST1: embed-level pre-filter (set in settings) restricts the source to the
    // chosen classes/instructors/locations before the visitor's own pills apply.
    // The settings UI stores IDs — event-grouping ids, provider user ids, and
    // location ids — so match by those. Empty/absent array = pass-through.
    const preClass = o.filterClassIds?.length
      ? new Set(o.filterClassIds)
      : null;
    const preInst = o.filterInstructors?.length
      ? new Set(o.filterInstructors)
      : null;
    const preLoc = o.filterLocations?.length
      ? new Set(o.filterLocations)
      : null;
    const includeOneOff = o.includeOneOffEvents !== false;
    return this.schedule.filter(
      (c) =>
        // Match the class's event-grouping id (falling back to its name when a
        // row has no grouping id — e.g. mock rows), the provider user ids
        // (compared as strings, since the settings store them as strings), and
        // the location id. One-offs have no grouping: when includeOneOffEvents
        // is on they are additive and still pass a class/grouping pre-filter.
        (!c.isStandaloneOneOff || includeOneOff) &&
        (!preClass ||
          preClass.has(c.eventGroupingId ?? c.name) ||
          (includeOneOff && !!c.isStandaloneOneOff)) &&
        (!preInst ||
          !!c.providerUserIds?.some((id) => preInst.has(String(id)))) &&
        (!preLoc || (!!c.locationId && preLoc.has(c.locationId))) &&
        (!this.classFilter || c.name === this.classFilter) &&
        (!this.instructorFilter || c.instructor === this.instructorFilter) &&
        (!this.locationFilter || c.location === this.locationFilter) &&
        (!this.opts.hideCancelled || !c.cancelled),
    );
  }

  /**
   * The calendar day a class is filed under, resolved in the SAME zone its
   * time is displayed in.
   *
   * Every day bucket in the component goes through this: the list's day
   * sections, the strip's "has classes" dots, the week columns and the calendar
   * cells. 'c.date' alone is a raw instant, so reading its local parts buckets
   * by the *device's* day — which for a late-evening class disagrees with the
   * "9:30 PM" the row actually prints, and with 'today'. One zone for the
   * label and the bucket, or the strip highlights a day that has no section.
   */
  private dayOf(c: MockClass): Date {
    return startOfZonedDay(occurrenceInstant(c), this.effectiveTz);
  }

  private startDate(): Date {
    const s = this.opts.startDate;
    if (s) {
      const d = new Date(s + "T00:00:00");
      if (!isNaN(d.getTime())) return d;
    }
    return this.today;
  }

  /**
   * Capacity vocabulary, mirroring the dashboard-customer booking flow:
   *  - waitlist with people:  "⏳ {n} on the waitlist"
   *  - waitlist, empty:       "✨ Be the first to join the waitlist!"
   *  - low availability:      "🔥 Only {n} left!"  (spotsLeft <= 2 OR <= 33% of total)
   *  - otherwise:             "{n} left"
   */
  private capacityText(
    c: MockClass,
  ): { text: string; waiting: boolean } | null {
    return c.externalTickets ? null : capacityBadge(c, this.opts);
  }

  // ── Rendering ──

  protected renderContent(_config: EmbedConfig): TemplateResult {
    if (this.bookingClass) {
      const c = this.bookingClass;
      if (
        this.runtime.surface === "dashboard-customer" &&
        this.runtime.commerce &&
        c.eventSlug
      ) {
        const offering: CommerceClassOfferingRefDto = {
          kind: CommerceOfferingKind.Class,
          offeringKey: `class:${c.eventSlug}`,
          occurrenceKey: c.id,
        };
        return html`<zipper-commerce-checkout
          .runtime=${this.runtime}
          .offering=${offering}
          .requestedAction=${this.bookingAction}
          .orgId=${this.config?.orgId ?? 0}
          .title=${c.name}
          .subtitle=${[
            `${this.formatDayHeader(this.dayOf(c))} · ${this.displayTime(c)}`,
            c.instructor,
          ]
            .filter(Boolean)
            .join(" · ")}
          .stripe=${this.config?.stripe}
          @zipper-commerce-cancel=${() => {
            if (this.bookingClass === c) this.bookingClass = null;
          }}
          @zipper-commerce-success=${() =>
            this.dispatchEvent(
              new CustomEvent("zipper-book", {
                detail: { classId: c.id, name: c.name },
                bubbles: true,
                composed: true,
              }),
            )}
        ></zipper-commerce-checkout>`;
      }
      return html`<zipper-booking-checkout
        .className=${c.name}
        .classWhen=${[
          `${this.formatDayHeader(this.dayOf(c))} · ${this.displayTime(c)}`,
          this.rowTzAbbrev(c),
        ]
          .filter(Boolean)
          .join(" ")}
        .instructorName=${c.instructor}
        .duration=${c.duration}
        .image=${
          (this.opts.thumbnail === "class" ? c.classPhoto : c.photo) ?? ""
        }
        .bookButtonText=${this.opts.buttonText || "Book class"}
        .tiers=${this.tiersByClassId[c.id] ?? []}
        .stripe=${this.config?.stripe}
        .orgId=${this.config?.orgId ?? 0}
        .accent=${this.config?.theme?.accent ?? "#4F9DB5"}
        .requirePhone=${!!this.opts.requirePhone}
        .requiredSignupFields=${this.config?.requiredSignupFields}
        .currency=${this.config?.currency ?? ""}
        .hideRecipientBooking=${!!this.opts.hideRecipientBooking}
        .roomId=${c.roomId ?? ""}
        .startIso=${c.startIso ?? ""}
        .endIso=${c.endIso ?? ""}
        .orgSlug=${this.config?.orgSlug ?? ""}
        .eventSlug=${c.eventSlug ?? ""}
        .activityId=${c.id}
        .classForms=${c.forms ?? []}
        @zipper-booking-close=${() => (this.bookingClass = null)}
      ></zipper-booking-checkout>`;
    }
    if (this.scheduleLoading) {
      return this.renderSkeleton();
    }
    const o = this.opts;
    const views = this.enabledViews();
    const showSwitcher = !!o.viewSwitcherEnabled && views.length >= 2;
    const isList = this.isListView;
    // The filter row travels WITH the day navigator as one sticky block in
    // list view, so filtering and date navigation stay reachable together
    // while scrolling a long schedule (previously only the strip stuck and the
    // filters scrolled away above it). Without the switcher there's no
    // controls row of its own, so the filters are hoisted out of the title row
    // into the sticky block to keep the behaviour identical either way.
    const hoistFilters = isList && !showSwitcher && !o.hideFilters;
    return html`
      <div class="wrap">
        ${this.renderFilterSheet()}
        ${this.renderHead(showSwitcher, hoistFilters)}
        ${
          this.scheduleError
            ? html` <div class="zui-state" role="alert">
                Could not load the full schedule. Please retry.
                <button
                  class="schedule-retry zui-btn-secondary"
                  type="button"
                  @click=${() => this.retrySchedule()}
                >
                  Retry
                </button>
              </div>`
            : nothing
        }
        ${
          isList
            ? html`<div class="sticky-head">
                  ${
                    showSwitcher
                      ? this.renderControls(views)
                      : hoistFilters
                        ? html`<div class="controls">
                            ${this.renderFilters()}
                          </div>`
                        : nothing
                  }
                  ${this.renderStrip(showSwitcher)}
                </div>
                ${this.scheduleError && this.schedule.length === 0 ? nothing : this.renderList()}`
            : html`${showSwitcher ? this.renderControls(views) : nothing}
              ${this.view === "month" ? this.renderMonth() : this.renderWeek()}`
        }
      </div>
    `;
  }

  private async retrySchedule(): Promise<void> {
    const failure = this.scheduleError;
    if (!failure) return;
    this.scheduleError = null;
    if (failure.kind === "initial") {
      this.scheduleLoading = true;
      await this.loadSchedule(this.loadedAuthorityKey);
    } else if (failure.kind === "month") {
      await this.loadCalendarMonth(failure.month);
    } else {
      await this.loadMore();
      if (this.isBoundedList && this.nextDayStatus === "error" && !this.scheduleError) {
        await this.findNextPeriod();
      }
    }
  }

  /** Views offered in the switcher, in display order,
   * minus any disabled by options. Folds in the old Calendar embed (G2). */
  private enabledViews(): { id: ViewMode; label: string }[] {
    const o = this.opts;
    return [
      !o.hideCalendarView && { id: "month" as ViewMode, label: "Calendar" },
      !o.hideDayView && { id: "day" as ViewMode, label: "Day" },
      !o.hideWeekListView && { id: "list-week" as ViewMode, label: "Week list" },
      !o.hideWeekView && { id: "week" as ViewMode, label: "Week" },
      !o.hideListView && { id: "list" as ViewMode, label: "List" },
    ].filter(Boolean) as { id: ViewMode; label: string }[];
  }

  private renderSignin(): TemplateResult {
    if (this.signedInName) {
      // A4: reuse the exact "signin zui-btn-secondary" classes + icon the
      // signed-out state uses below, rather than hand-copying font-size/weight/
      // letter-spacing values, so Sign out can never drift out of sync with
      // Sign in again.
      return html`<button
        class="signin zui-btn-secondary"
        type="button"
        @click=${() => this.logout()}
      >
        ${ICON_SIGNIN} Sign out
      </button>`;
    }
    return html`<button
      class="signin zui-btn-secondary"
      type="button"
      @click=${() =>
        openSignIn(
          this.config?.orgId ?? 0,
          this.config?.theme?.accent ?? "#4F9DB5",
          (name) => (this.signedInName = name || "there"),
        )}
    >
      ${ICON_SIGNIN} Sign in
    </button>`;
  }

  /**
   * Title row. 'hoistFilters' = the filters have been moved into the sticky
   * block below (list view without a switcher), so this row must not render
   * them a second time.
   */
  private renderHead(
    showSwitcher: boolean,
    hoistFilters = false,
  ): TemplateResult {
    const o = this.opts;
    return html`
      <div class="head">
        ${
          o.hideTitle
            ? html`<span></span>`
            : html`<h1 class="title">
                ${o.headerTitle?.trim() || "Book a Class"}
              </h1>`
        }
        ${
          showSwitcher
            ? o.hideSignIn
              ? nothing
              : this.renderSignin()
            : o.hideFilters || hoistFilters
              ? nothing
              : this.renderFilters()
        }
      </div>
    `;
  }

  /** Filters left, view choice right. Mobile uses a labeled native select so
   * Day and the extending List remain directly selectable. */
  private renderControls(
    views: { id: ViewMode; label: string }[] = this.enabledViews(),
  ): TemplateResult {
    return html`
      <div class="controls">
        ${this.opts.hideFilters ? html`<span></span>` : this.renderFilters()}
        <div class="switch-wrap">
          <div class="switcher" role="group" aria-label="View">
            ${views.map(
              (v) =>
                html`<button
                  type="button"
                  aria-pressed=${this.view === v.id}
                  @click=${() => (this.view = v.id)}
                >
                  ${v.label}
                </button>`,
            )}
          </div>
          <select
            class="view-select"
            aria-label="View"
            @change=${(event: Event) => {
              this.view = (event.target as HTMLSelectElement).value as ViewMode;
            }}
          >
            ${views.map((v) => html`<option value=${v.id}>${v.label}</option>`)}
          </select>
        </div>
      </div>
    `;
  }

  /** Distinct filter option lists derived from the loaded schedule. */
  private filterOptions(): {
    classNames: string[];
    instructors: string[];
    locations: string[];
  } {
    return {
      classNames: [...new Set(this.schedule.map((c) => c.name))].filter(
        Boolean,
      ),
      instructors: [...new Set(this.schedule.map((c) => c.instructor))]
        .filter(Boolean)
        .sort(),
      locations: [...new Set(this.schedule.map((c) => c.location))]
        .filter(Boolean)
        .sort(),
    };
  }

  private filterPill(
    value: string,
    allLabel: string,
    options: string[],
    onChange: (v: string) => void,
  ): TemplateResult {
    return html`
      <zipper-filter-pill
        .value=${value}
        .label=${allLabel}
        .options=${options}
        .accent=${this.config?.theme?.accent ?? "#4F9DB5"}
        @zipper-filter-change=${(e: CustomEvent<{ value: string }>) =>
          onChange(e.detail.value)}
      ></zipper-filter-pill>
    `;
  }

  /** True when any filter is narrowing the list — drives the mobile trigger's
   * active styling + enables "Clear all". */
  private get anyFilterActive(): boolean {
    return !!(
      this.classFilter ||
      this.instructorFilter ||
      (!this.opts.hideLocationFilter && this.locationFilter)
    );
  }

  private renderFilters(): TemplateResult {
    const o = this.opts;
    const { classNames, instructors, locations } = this.filterOptions();
    // Desktop: a row of pills. Mobile: a single "Filter" trigger (the row is
    // hidden via CSS) that opens the filter sheet. Both are always rendered so
    // the CSS media query picks whichever fits (MOB-26).
    return html`
      <div class="filters">
        ${this.filterPill(
          this.classFilter,
          "All classes",
          classNames,
          (v) => (this.classFilter = v),
        )}
        ${this.filterPill(
          this.instructorFilter,
          "All instructors",
          instructors,
          (v) => (this.instructorFilter = v),
        )}
        ${
          o.hideLocationFilter
            ? nothing
            : this.filterPill(
                this.locationFilter,
                "All locations",
                locations,
                (v) => (this.locationFilter = v),
              )
        }
        ${this.renderTzSwitch()}
      </div>
      <button
        class="filter-trigger ${this.anyFilterActive ? "active" : ""}"
        type="button"
        aria-haspopup="dialog"
        @click=${() => (this.filterSheetOpen = true)}
      >
        ${ICON_FILTER}<span>Filter</span>
      </button>
    `;
  }

  /** Mobile filter sheet (MOB-26): every filter reachable from the single
   * "Filter" trigger, reusing the same filter-pill control as the desktop row
   * so all filtering stays available on a phone. */
  private renderFilterSheet(): TemplateResult | typeof nothing {
    if (!this.filterSheetOpen) return nothing;
    const o = this.opts;
    const { classNames, instructors, locations } = this.filterOptions();
    return html`
      <div
        class="fs-overlay"
        popover="manual"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.filterSheetOpen = false;
        }}
      >
        <div
          class="fs-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Filters"
        >
          <div class="fs-head">
            <span>Filters</span>
            <button
              class="fs-close"
              aria-label="Close"
              @click=${() => (this.filterSheetOpen = false)}
            >
              ✕
            </button>
          </div>
          <div class="fs-body">
            <div class="fs-row">
              <span class="fs-label">Class</span>
              ${this.filterPill(
                this.classFilter,
                "All classes",
                classNames,
                (v) => (this.classFilter = v),
              )}
            </div>
            <div class="fs-row">
              <span class="fs-label">Instructor</span>
              ${this.filterPill(
                this.instructorFilter,
                "All instructors",
                instructors,
                (v) => (this.instructorFilter = v),
              )}
            </div>
            ${
              o.hideLocationFilter
                ? nothing
                : html`<div class="fs-row">
                    <span class="fs-label">Location</span>
                    ${this.filterPill(
                      this.locationFilter,
                      "All locations",
                      locations,
                      (v) => (this.locationFilter = v),
                    )}
                  </div>`
            }
            ${
              this.canSwitchTz
                ? html`<div class="fs-row">
                    <span class="fs-label">Time zone</span>
                    ${this.renderTzSwitch()}
                  </div>`
                : nothing
            }
          </div>
          <div class="fs-foot">
            <button
              class="fs-clear"
              ?disabled=${!this.anyFilterActive}
              @click=${() => this.clearFilters()}
            >
              Clear all
            </button>
            <button
              class="fs-done"
              @click=${() => (this.filterSheetOpen = false)}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private clearFilters(): void {
    this.classFilter = "";
    this.instructorFilter = "";
    this.locationFilter = "";
  }

  private renderStrip(showSwitcher: boolean): TemplateResult | typeof nothing {
    if (this.opts.hideDateStrip) return nothing;
    const anchor = this.weekAnchor ?? this.today;
    const dates = Array.from({ length: 7 }, (_, i) => addDays(anchor, i));
    const atToday = sameDay(this.activeDate, this.today) && !this.weekAnchor;
    const hasClasses = (d: Date) =>
      this.filtered().some((c) => sameDay(this.dayOf(c), d));
    return html`
      ${this.renderMonthPicker()}
      <div class="strip-wrap">
        <div class="strip-bar">
          <button
            class="month-label"
            type="button"
            @click=${() => this.openMonthPicker()}
          >
            ${MONTHS[this.activeDate.getMonth()]}
            ${this.activeDate.getFullYear()} ${ICON_CARET}
          </button>
          <div class="strip-bar-actions">
            ${
              atToday
                ? nothing
                : html`<button
                    class="today-pill"
                    @click=${() => this.goToday()}
                  >
                    Today
                  </button>`
            }
            ${
              !showSwitcher && !this.opts.hideSignIn
                ? this.renderSignin()
                : nothing
            }
          </div>
        </div>
        <div class="strip">
          <button
            class="arrow"
            aria-label="Previous week"
            @click=${() => this.shiftWeek(-1)}
          >
            ${ICON_CHEVRON_L}
          </button>
          <div class="days">
            ${dates.map((d) => {
              const active = sameDay(d, this.activeDate);
              const today = sameDay(d, this.today);
              const past = this.isPastDay(d);
              const dotClass = hasClasses(d)
                ? today
                  ? "dot on today"
                  : "dot on"
                : "dot";
              return html`
                <button
                  class="day ${past ? "past" : ""}"
                  aria-selected=${active}
                  @click=${() => this.jumpToDay(d)}
                >
                  <span class="cell">
                    <span class="dow">
                      <span class="dow-full">${WEEKDAYS[d.getDay()]}</span>
                      <span class="dow-short"
                        >${WEEKDAYS[d.getDay()].slice(0, 2)}</span
                      >
                    </span>
                    <span class="num">${d.getDate()}</span>
                  </span>
                  <span class=${dotClass}></span>
                </button>
              `;
            })}
          </div>
          <button
            class="arrow"
            aria-label="Next week"
            @click=${() => this.shiftWeek(1)}
          >
            ${ICON_CHEVRON_R}
          </button>
        </div>
      </div>
    `;
  }

  /** Open the month/year picker modal from the strip's month label. */
  private openMonthPicker(): void {
    this.pickerYear = this.activeDate.getFullYear();
    this.pickerMonth = null;
    this.monthPickerOpen = true;
  }

  private closeMonthPicker(): void {
    this.monthPickerOpen = false;
    this.pickerMonth = null;
  }

  /** Month chosen — show the day grid for that month before jumping. */
  private selectPickerMonth(year: number, month: number): void {
    this.pickerYear = year;
    this.pickerMonth = month;
  }

  /** Day chosen — jump the strip + list and dismiss the picker (SCH-04). */
  private jumpToPickerDay(year: number, month: number, day: number): void {
    const target = new Date(year, month, day);
    this.closeMonthPicker();
    // In the calendar view the month-label dropdown navigates the grid's month
    // (and selects the day); the list/week views jump their strip to the day.
    if (this.view === "month") {
      this.monthCursor = new Date(year, month, 1);
      this.selectedMonthDay = target;
      this.classPopover = null;
      void this.loadCalendarMonth(this.monthCursor);
      return;
    }
    this.weekAnchor = sameDay(target, this.today) ? null : target;
    this.jumpToDay(target);
  }

  /**
   * Advance the 7-day date strip (and highlighted day) by a full week (SCH-03).
   * The strip is a rolling window starting at the week anchor, so shifting the
   * anchor by ±7 moves the whole strip a week rather than a single day.
   */
  private shiftWeek(delta: number): void {
    // Advance a FULL week while keeping the SAME weekday selected (SCH: S2).
    // The old logic jumped the active day to the week's start day, so the
    // selection landed on an odd weekday "at odd intervals". Shift the active
    // day and the strip window together by 7 so the column position is stable,
    // then auto-scroll the list to that same weekday in the new week.
    const step = delta * 7;
    const nextActive = addDays(this.activeDate, step);
    const nextAnchor = addDays(this.weekAnchor ?? this.today, step);
    // Back on today's week → null anchor so the strip follows "today" and the
    // Today pill hides; otherwise anchor the strip to the new week.
    this.weekAnchor = sameDay(nextAnchor, this.today) ? null : nextAnchor;
    this.jumpToDay(nextActive);
  }

  /**
   * Measure the sticky header block and publish its height as '--zui-sticky-h'
   * on the host, where the day sections' 'scroll-margin-top' picks it up.
   *
   * Measured rather than hardcoded because the block's height is not a
   * constant: the filter pills wrap onto a second row at narrow widths, the
   * view switcher collapses on mobile, and the "Today" pill appears and
   * disappears. A fixed clearance was already drifting; now that the filters
   * live inside the sticky block (they used to scroll away) a stale constant
   * would land every scroll target underneath the chrome.
   *
   * Driving the browser's own 'scroll-margin' — rather than computing a
   * 'scrollTo(y - offset)' ourselves — is deliberate: these embeds run on
   * third-party pages where the scrolling ancestor may be any div, and
   * 'scroll-margin' is applied by whichever scroller 'scrollIntoView' actually
   * uses. Manual offset math would assume the window and silently misfire.
   */
  private syncStickyOffset(): void {
    const head = this.renderRoot.querySelector(".sticky-head");
    if (!head) return;
    const h = Math.round(head.getBoundingClientRect().height);
    // happy-dom (and any pre-layout frame) reports 0 — leave the CSS fallback
    // in place rather than pinning the clearance to nothing.
    if (h > 0) this.style.setProperty("--zui-sticky-h", `${h}px`);
    if (
      typeof ResizeObserver !== "undefined" &&
      head !== this.stickyResizeTarget
    ) {
      this.stickyResizeObserver?.disconnect();
      this.stickyResizeTarget = head;
      this.stickyResizeObserver = new ResizeObserver(() => {
        if (!this.isConnected || !this.isListView || this.bookingClass)
          return;
        this.syncStickyOffset();
        this.observeDaySections();
      });
      this.stickyResizeObserver.observe(head);
    }
  }

  /**
   * The rendered day '<section>' for 'd', or — when that day isn't rendered —
   * the first one AFTER it.
   *
   * The fall-forward is what makes "Today" work on a day the studio has no
   * classes: there's no 'Today' section to scroll to, so it lands on the next
   * day that has one (the same "next event" the first-paint anchor picks).
   * Previously that case fell back to scrolling '.strip-wrap' into view, which
   * is *sticky* and therefore already pinned at the top of the viewport — so
   * 'scrollIntoView' had nothing to do and the page visibly didn't move, which
   * is exactly the reported "Today scrolls the day navigator but not the page".
   */
  private daySectionOnOrAfter(d: Date): Element | null {
    const sections = Array.from(
      this.renderRoot.querySelectorAll("section[data-offset]"),
    );
    const key = d.toDateString();
    const exact = sections.find((s) => s.getAttribute("data-offset") === key);
    if (exact) return exact;
    const target = startOfDay(d).getTime();
    let best: Element | null = null;
    let bestTime = Infinity;
    for (const s of sections) {
      const day = new Date(s.getAttribute("data-offset") ?? "");
      if (isNaN(day.getTime())) continue;
      const t = startOfDay(day).getTime();
      if (t >= target && t < bestTime) {
        bestTime = t;
        best = s;
      }
    }
    return best;
  }

  /**
   * The ONE scroll path for every "take me to this day" action — the week
   * strip's day pills, its arrows, the month picker, and the Today pill. They
   * used to have three separate implementations that drifted apart on exactly
   * this: which node gets scrolled, and whether the sticky chrome is accounted
   * for. Initial positioning has a separate row-specific path because it lands
   * on a timeslot rather than a day heading.
   *
   * Scrolls the day's own '<section>', which is the element carrying the
   * '<h2 class="day-head">' heading — not a row inside it — so the heading
   * lands at the top rather than just above the fold.
   */
  private scrollToDay(d: Date, generation = this.navigationGeneration): void {
    void this.updateComplete.then(() => {
      if (
        !this.isConnected ||
        this.bookingClass ||
        generation !== this.navigationGeneration
      )
        return;
      this.syncStickyOffset();
      const target = this.daySectionOnOrAfter(d) ?? this;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  /** Reset the strip back to today's week, and bring the list with it. */
  private goToday(): void {
    this.weekAnchor = null;
    this.monthCursor = null;
    this.selectedMonthDay = null;
    this.classPopover = null;
    this.jumpToDay(this.today);
  }

  private isPastDay(d: Date): boolean {
    return startOfDay(d) < startOfDay(this.today);
  }

  /** Whether 'c' has already ended, relative to "now" — gates the Book
   * button + capacity text off for past occurrences. Compares real instants via
   * the pure helper (unit-tested in schedule-helpers.test.ts), so a class
   * earlier *today* (a 9am session viewed at 2pm) counts as past. Uses
   * 'nowInstant', NOT 'today': 'today' is midnight of the current day, against
   * which every one of today's classes would look like it's still to come. */
  private isPastClass(c: MockClass): boolean {
    return isPastOccurrence(c, this.nowInstant);
  }

  private renderMonthPicker(): TemplateResult | typeof nothing {
    if (!this.monthPickerOpen) return nothing;
    const year = this.pickerYear || this.activeDate.getFullYear();
    const onDayStep = this.pickerMonth !== null;
    return html`
      <div
        class="mp-overlay"
        popover="manual"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) this.closeMonthPicker();
        }}
      >
        <div
          class="mp-sheet"
          role="dialog"
          aria-modal="true"
          aria-label=${onDayStep ? "Choose a day" : "Choose a month"}
        >
          <div class="mp-head">
            ${
              onDayStep
                ? html`<button
                    class="mp-back"
                    type="button"
                    @click=${() => (this.pickerMonth = null)}
                  >
                    ← ${MONTHS[this.pickerMonth!]}
                  </button>`
                : html`<button
                    class="arrow"
                    aria-label="Previous year"
                    @click=${() => (this.pickerYear = year - 1)}
                  >
                    ${ICON_CHEVRON_L}
                  </button>`
            }
            <span class="mp-year">${year}</span>
            ${
              onDayStep
                ? html`<span style="width:32px"></span>`
                : html`<button
                    class="arrow"
                    aria-label="Next year"
                    @click=${() => (this.pickerYear = year + 1)}
                  >
                    ${ICON_CHEVRON_R}
                  </button>`
            }
          </div>
          ${
            onDayStep
              ? this.renderMonthPickerDays(year, this.pickerMonth!)
              : html`<div class="mp-grid">
                  ${MONTHS.map((m, i) => {
                    const sel =
                      i === this.activeDate.getMonth() &&
                      year === this.activeDate.getFullYear();
                    return html`<button
                      class="mp-month ${sel ? "sel" : ""}"
                      type="button"
                      @click=${() => this.selectPickerMonth(year, i)}
                    >
                      ${m}
                    </button>`;
                  })}
                </div>`
          }
        </div>
      </div>
    `;
  }

  private renderMonthPickerDays(year: number, month: number): TemplateResult {
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const lead = first.getDay();
    const start = this.startDate();
    const startSod = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const cells: TemplateResult[] = [];
    for (let i = 0; i < lead; i++) {
      cells.push(html`<div class="mp-day empty"></div>`);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const beforeStart = date < startSod;
      const isToday = sameDay(date, this.today);
      const isActive = sameDay(date, this.activeDate);
      const dayClasses = [
        "mp-day",
        isToday ? "today" : "",
        isActive ? "sel" : "",
      ]
        .filter(Boolean)
        .join(" ");
      cells.push(html`
        <button
          class=${dayClasses}
          type="button"
          ?disabled=${beforeStart}
          aria-label=${`${MONTHS[month]} ${d}, ${year}`}
          @click=${() => this.jumpToPickerDay(year, month, d)}
        >
          ${d}
        </button>
      `);
    }
    return html`
      <div class="mp-day-grid">
        ${WEEKDAYS.map((w) => html`<div class="mp-dow">${w.slice(0, 2)}</div>`)}
        ${cells}
      </div>
    `;
  }

  /** Step the calendar view by whole months. */
  private shiftMonth(delta: number): void {
    const ref = this.monthCursor ?? this.selectedMonthDay ?? this.startDate();
    this.monthCursor = new Date(ref.getFullYear(), ref.getMonth() + delta, 1);
    this.selectedMonthDay = null;
    this.classPopover = null;
    void this.loadCalendarMonth(this.monthCursor);
  }

  /** Keep the visible week containing `d` (compare by calendar day so the
   * strip's time-of-day doesn't skew the math). Shared by manual day-picks
   * (which also scroll) and scroll-driven highlighting (which must not). */
  private moveWeekAnchorFor(d: Date): void {
    const sod = (x: Date) =>
      new Date(x.getFullYear(), x.getMonth(), x.getDate());
    const anchor = this.weekAnchor ?? this.today;
    const diff = Math.round(
      (sod(d).getTime() - sod(anchor).getTime()) / 86400000,
    );
    if (diff < 0) this.weekAnchor = d;
    else if (diff > 6) this.weekAnchor = addDays(d, -6);
  }

  private jumpToDay(d: Date): void {
    const generation = this.beginDateNavigation();
    this.activeDate = d;
    this.moveWeekAnchorFor(d);
    // PLAT-6790: the day may be past the loaded window; scroll once it lands.
    const needsLoad =
      !!this.loadedUntil && d.getTime() >= this.loadedUntil.getTime();
    const through = this.isWeekList ? addDays(this.listWeekStart(), 6)
      : addDays(d, this.isSingleDayList ? 1 : 6);
    if (needsLoad) {
      void this.ensureLoadedThrough(through).then(async () => {
        await this.updateComplete;
        if (
          generation === this.navigationGeneration &&
          sameDay(this.activeDate, d)
        )
          this.scrollToDay(d, generation);
      });
      return;
    }
    this.scrollToDay(d, generation);
    void this.ensureLoadedThrough(through);
  }

  /**
   * Advance the highlighted day pill as the list scrolls (infinite scroll
   * never otherwise moves `activeDate`/`weekAnchor`). Driven by the
   * IntersectionObserver in {@link observeDaySections} — unlike
   * {@link jumpToDay}, this never scrolls, or the observer would fight the
   * user's own scrolling.
   */
  private setActiveDateFromScroll(d: Date): void {
    // Muted until the visitor actually scrolls. The observer's band is filled
    // the moment the list first paints, so without this it would immediately
    // re-point `activeDate` at whichever section the host page happens to have
    // in view — silently undoing the deterministic "today / next event" anchor
    // with something viewport-dependent.
    if (!this.tracksScrollSelection || !this.userScrolled || this.explicitDateNavigation) return;
    if (sameDay(d, this.activeDate)) return;
    this.activeDate = d;
    if (!this.isWeekList) this.moveWeekAnchorFor(d);
  }

  /**
   * Watch each rendered day `<section data-offset>` and highlight the pill
   * for whichever day section currently occupies the "active" band just
   * below the sticky date strip — the same landing spot jumpToDay() scrolls
   * to. Re-attached on every update while in list view since the sections
   * backing DOM nodes are recreated on every render.
   */
  private observeDaySections(): void {
    if (!this.tracksScrollSelection) {
      this.teardownDayObserver();
      return;
    }
    const sections = Array.from(
      this.renderRoot.querySelectorAll("section[data-offset]"),
    );
    const keys = sections.map((s) => s.getAttribute("data-offset")).join("|");
    // Reuse the live observer while the rendered day sections are unchanged so a
    // scroll-driven activeDate update (which re-renders → updated()) doesn't
    // tear the observer down and drop its tracked intersection state mid-scroll.
    const root = this.findScrollRoot();
    const head = this.renderRoot.querySelector(".sticky-head");
    const stickyHeight = head?.getBoundingClientRect().height || 96;
    const offset =
      parseFloat(
        getComputedStyle(this).getPropertyValue("--zui-sticky-offset"),
      ) || 0;
    const inset = Math.round(stickyHeight + offset + 8);
    const rootHeight = root?.clientHeight || window.innerHeight;
    const bottom = Math.max(
      0,
      Math.round(
        rootHeight -
          Math.min(rootHeight, inset + Math.max(48, rootHeight * 0.35)),
      ),
    );
    const margin = `-${inset}px 0px -${bottom}px 0px`;
    if (
      this.daySectionObserver &&
      keys === this.observedSectionKeys &&
      root === this.observedScrollRoot &&
      margin === this.observedRootMargin
    )
      return;
    this.teardownDayObserver();
    this.observedScrollRoot = root;
    this.observedRootMargin = margin;
    if (!sections.length) return;
    this.observedSectionKeys = keys;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!this.isConnected || this.daySectionObserver !== observer) return;
        // Maintain the FULL set of intersecting sections across callbacks. Each
        // callback only reports sections whose intersection *changed*, so the
        // previous "topmost of this batch" logic stuck a day behind whenever the
        // current day scrolled out while the next day was already visible.
        for (const e of entries) {
          if (e.isIntersecting) this.intersectingSections.add(e.target);
          else this.intersectingSections.delete(e.target);
        }
        let topEl: Element | null = null;
        let topY = Infinity;
        const stickyRect = head?.getBoundingClientRect();
        const visibleTop =
          stickyRect && stickyRect.height > 0
            ? stickyRect.bottom + 8
            : (root?.getBoundingClientRect().top ?? 0) + inset;
        for (const el of this.intersectingSections) {
          if (!el.isConnected) {
            this.intersectingSections.delete(el);
            continue;
          }
          const rect = el.getBoundingClientRect();
          // A previous day's content concealed by sticky controls cannot select that day.
          if (rect.bottom <= visibleTop) continue;
          const y = rect.top;
          if (y < topY) {
            topY = y;
            topEl = el;
          }
        }
        if (!topEl) return;
        const key = topEl.getAttribute("data-offset");
        if (!key) return;
        const d = new Date(key);
        if (!isNaN(d.getTime())) this.setActiveDateFromScroll(d);
      },
      {
        root,
        rootMargin: margin,
        threshold: 0,
      },
    );
    this.daySectionObserver = observer;
    for (const section of sections) observer.observe(section);
  }

  private teardownDayObserver(): void {
    this.daySectionObserver?.disconnect();
    this.daySectionObserver = null;
    this.intersectingSections.clear();
    this.observedSectionKeys = "";
  }

  private listStartDate(): Date {
    return computeListStartDate(this.today, this.startDate(), this.activeDate);
  }

  private renderList(): TemplateResult {
    const start = startOfDay(this.listStartDate()).getTime();
    const weekStart = this.listWeekStart().getTime();
    const weekEnd = addDays(this.listWeekStart(), 7).getTime();
    const classes = this.filtered().filter(
      (c) => this.isSingleDayList
        ? sameDay(this.dayOf(c), this.activeDate)
        : this.isWeekList
          ? this.dayOf(c).getTime() >= weekStart && this.dayOf(c).getTime() < weekEnd
        : this.dayOf(c).getTime() >= start,
    );
    // group by day (in the display zone — see dayOf)
    const byDay = new Map<string, MockClass[]>();
    for (const c of classes) {
      const key = this.dayOf(c).toDateString();
      (byDay.get(key) ?? byDay.set(key, []).get(key)!).push(c);
    }
    if (byDay.size === 0) {
      if (this.isBoundedList) return this.renderEmptyPeriod();
      return this.loadingMore
        ? html`<div class="zui-state">Loading classes…</div>`
        : html`<div class="zui-state">No upcoming classes.</div>`;
    }
    return html`
      <div class="days-list">
        ${[...byDay.entries()].map(
          ([day, items]) => html`
            <section data-offset=${day}>
              ${
                this.opts.hideDateStrip
                  ? nothing
                  : html`<h2 class="day-head">
                      ${this.formatDayHeader(new Date(day))}
                    </h2>`
              }
              ${items.map((c) => this.renderRow(c))}
            </section>
          `,
        )}
      </div>
      ${
        // PLAT-6790: the observer on this marker loads the next window.
        !this.isBoundedList && this.canLoadMore()
          ? html`<div class="list-more" aria-live="polite">
              ${this.loadingMore ? "Loading more classes…" : ""}
            </div>`
          : nothing
      }
    `;
  }

  private renderEmptyPeriod(): TemplateResult {
    const searching = this.nextDayStatus === "searching";
    return html`<div class="zui-state single-day-state" role="status" aria-live="polite" aria-busy=${searching}>
      <p>${this.nextDayStatus === "exhausted"
        ? "No upcoming sessions match your selection"
        : searching || this.loadingMore ? "Searching for sessions…"
          : this.isWeekList ? "Nothing available this week" : "Nothing available on this day"}</p>
      ${this.nextDayStatus !== "exhausted" && !this.scheduleError
        ? html`<button class="next-day zui-btn-secondary" type="button" ?disabled=${searching || this.loadingMore}
            @click=${() => this.findNextPeriod()}>${this.isWeekList ? "Go to next week with sessions" : "Go to next day with sessions"}</button>`
        : nothing}
    </div>`;
  }

  private renderRow(c: MockClass): TemplateResult {
    return renderScheduleRow(c, {
      options: this.opts,
      isPast: this.isPastClass(c),
      registrationClosed: !c.externalTickets && isRegistrationClosed(c, this.nowInstant),
      registrationNotYetOpen: !c.externalTickets && isNotYetOpen(c, this.nowInstant),
      capacity: this.capacityText(c),
      displayTime: this.displayTime(c),
      timezoneAbbreviation: this.rowTzAbbrev(c),
      expanded: this.expandedRowIds.has(c.id),
      bioExpanded: this.expandedBioIds.has(c.id),
      descriptionLoading: this.descriptionLoadingIds.has(c.id),
      descriptionText: c.description ?? this.descriptionCache[c.id],
      onToggleDescription: () => this.toggleRowExpanded(c),
      onToggleBio: () => this.toggleBioExpanded(c),
      onBook: () => this.onBook(c),
      onBuyout: () => this.onBuyout(c),
    });
  }

  /** G2: Week view — 7 day-columns of class cards for the visible week. Reuses
   * the strip's week window (weekAnchor ?? startDate) and the same
   * prev/next-week + Today controls as the list strip. */
  private renderWeek(): TemplateResult {
    const start = this.weekAnchor ?? this.startDate();
    const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
    const end = days[6];
    const classes = this.filtered();
    const range =
      start.getMonth() === end.getMonth()
        ? `${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()}–${end.getDate()}`
        : `${MONTHS[start.getMonth()].slice(0, 3)} ${start.getDate()} – ${MONTHS[
            end.getMonth()
          ].slice(0, 3)} ${end.getDate()}`;
    return html`
      <div class="wk">
        <div class="wk-nav">
          <div class="wk-range">
            <button
              class="arrow"
              aria-label="Previous week"
              @click=${() => this.shiftWeek(-1)}
            >
              ${ICON_CHEVRON_L}
            </button>
            <span class="wk-label">${range}</span>
            <button
              class="arrow"
              aria-label="Next week"
              @click=${() => this.shiftWeek(1)}
            >
              ${ICON_CHEVRON_R}
            </button>
          </div>
          ${
            this.weekAnchor
              ? html`<button class="today-pill" @click=${() => this.goToday()}>
                  Today
                </button>`
              : nothing
          }
        </div>
        <div class="wk-grid">
          ${days.map((d) => {
            const dayClasses = classes.filter((c) => sameDay(this.dayOf(c), d));
            const isToday = sameDay(d, this.today);
            return html`
              <div class="wk-col">
                <div class="wk-colhead ${isToday ? "today" : ""}">
                  <span class="wk-dow">${WEEKDAYS[d.getDay()]}</span>
                  <span class="wk-date">${d.getDate()}</span>
                </div>
                <div class="wk-cards">
                  ${
                    dayClasses.length
                      ? dayClasses.map((c) => this.renderWeekCard(c))
                      : html`<div class="wk-empty">—</div>`
                  }
                </div>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  /** A single class card inside a Week-view day column. */
  private renderWeekCard(c: MockClass): TemplateResult {
    const o = this.opts;
    const external = !!c.externalTickets && !!c.externalTicketUrl;
    const isWaitlist = !external && c.capacityState === "waitlist";
    const notYetOpen = !external && isNotYetOpen(c, this.nowInstant);
    const closed = this.isPastClass(c) || (!external && isRegistrationClosed(c, this.nowInstant));
    const cap = closed ? null : this.capacityText(c);
    const label = notYetOpen
      ? "Not yet open"
      : (o.buttonText ??
        (c.offeringKind === "program"
          ? "View program"
          : isWaitlist
            ? "Join waitlist"
            : "Book"));
    const tzAbbr = this.rowTzAbbrev(c);
    return html`
      <div
        class="wk-card ${c.cancelled ? "cancelled" : ""} ${this.isPastClass(c) ? "past" : ""}"
      >
        <div class="wk-time">
          ${this.displayTime(c)}${
            tzAbbr ? html`<span class="wk-tz">${tzAbbr}</span>` : nothing
          }
        </div>
        <div class="wk-name">${c.name} ${renderOfferingBadge(c)}</div>
        ${
          o.hideInstructor || !c.instructor
            ? nothing
            : html`<div class="wk-meta">w/ ${c.instructor}</div>`
        }
        ${
          o.hideLocation || !c.location
            ? nothing
            : html`<div class="wk-meta">${c.location}</div>`
        }
        ${
          c.cancelled || !cap
            ? nothing
            : html`<div class="wk-cap ${cap.waiting ? "waiting" : ""}">
                ${cap.text}
              </div>`
        }
        ${
          c.cancelled
            ? html`<div class="wk-cancel">Cancelled</div>`
            : closed ? html`<div class="wk-cancel">${this.isPastClass(c) ? "Ended" : "Registration closed"}</div>`
            : external
              ? html`<a class="wk-book" href=${c.externalTicketUrl!} target="_self">${label}</a>`
            : html`${
                  c.offeringKind !== "program" && !notYetOpen && c.buyoutAvailable && typeof c.buyoutPriceMinor === "number"
                    ? html`<button
                        class="wk-book waitlist"
                        @click=${() => this.onBuyout(c)}
                      >
                        Buy out —
                        ${formatMinorAmount(c.buyoutPriceMinor, c.buyoutCurrency)}
                      </button>`
                    : nothing
                }<button
                  class="wk-book ${isWaitlist ? "waitlist" : ""}"
                  ?disabled=${notYetOpen || (c.offeringKind === "program" && !c.programBookingUrl)}
                  @click=${() => this.onBook(c)}
                >
                  ${label}
                </button>`
        }
      </div>
    `;
  }

  private renderMonth(): TemplateResult {
    const ref = this.monthCursor ?? this.selectedMonthDay ?? this.startDate();
    const year = ref.getFullYear();
    const month = ref.getMonth();
    const first = new Date(year, month, 1);
    // Full 6-week grid, Sunday-start, incl. prev/next-month overflow days.
    const gridStart = addDays(first, -first.getDay());
    const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    const classes = this.filtered();
    const selected = this.selectedMonthDay
      ? classes.filter((c) => sameDay(this.dayOf(c), this.selectedMonthDay!))
      : [];
    return html`
      <div class="cal">
        <div class="cal-toolbar">
          <div class="cal-tb-nav">
            <button
              class="cal-nav-btn"
              aria-label="Previous month"
              @click=${() => this.shiftMonth(-1)}
            >
              ${ICON_CHEVRON_L}
            </button>
            <button
              class="cal-nav-btn"
              aria-label="Next month"
              @click=${() => this.shiftMonth(1)}
            >
              ${ICON_CHEVRON_R}
            </button>
            <button
              class="cal-today-btn"
              type="button"
              @click=${() => this.goToday()}
            >
              Today
            </button>
          </div>
          <button
            class="cal-month-btn"
            type="button"
            @click=${() => this.openMonthPicker()}
          >
            ${MONTHS[month]} ${year} ${ICON_CARET}
          </button>
          <span class="cal-tb-spacer"></span>
        </div>
        ${this.renderMonthPicker()}
        <div class="cal-grid">
          <div class="cal-dow-row">
            ${WEEKDAYS.map(
              (w) => html`<div class="cal-dow">${w.slice(0, 3)}</div>`,
            )}
          </div>
          <div class="cal-cells">
            ${days.map((date) => {
              const inMonth = date.getMonth() === month;
              const isToday = sameDay(date, this.today);
              const past = this.isPastDay(date);
              const dayClasses = classes.filter((c) =>
                sameDay(this.dayOf(c), date),
              );
              return html`
                <div
                  class="cal-cell ${inMonth ? "" : "outside"} ${
                    past ? "past" : ""
                  }"
                  aria-selected=${
                    this.selectedMonthDay
                      ? sameDay(this.selectedMonthDay, date)
                      : false
                  }
                  @click=${() => this.selectMonthDay(date)}
                >
                  <span class="cal-daynum ${isToday ? "today" : ""}"
                    >${date.getDate()}</span
                  >
                  <div class="cal-bars">
                    ${dayClasses.map(
                      (c) =>
                        html`<button
                          class="cal-bar ${c.cancelled ? "cancelled" : ""}"
                          style=${classColorStyle(c.color)}
                          type="button"
                          title=${`${this.displayTime(c)} ${c.name} ${offeringLabel(c)}`}
                          @click=${(e: MouseEvent) =>
                            this.openClassPopover(e, c)}
                        >
                          <span class="cal-bar-time"
                            >${this.displayTime(c)}</span
                          >
                          ${c.name} ${renderOfferingBadge(c)}
                        </button>`,
                    )}
                  </div>
                  <div class="cal-dots">
                    ${dayClasses
                      .slice(0, 4)
                      .map((c) => html`<span
                          class="cal-dot"
                          style=${classColorStyle(c.color)}
                        ></span>`)}
                    ${
                      dayClasses.length > 4
                        ? html`<span class="cal-more"
                            >+${dayClasses.length - 4}</span
                          >`
                        : nothing
                    }
                  </div>
                </div>
              `;
            })}
          </div>
        </div>
        ${this.renderClassPopover()}
      </div>
      <div class="cal-detail">
        ${
          this.selectedMonthDay
            ? html`<h2 class="day-head">
                  ${this.formatDayHeader(this.selectedMonthDay)}
                </h2>
                ${
                  selected.length
                    ? selected.map((c) => this.renderRow(c))
                    : html`<div class="zui-state">No classes this day.</div>`
                }`
            : nothing
        }
      </div>
    `;
  }

  /** Anchored detail popover for a clicked class bar (desktop calendar). Shows
   * the same row info as list/mobile (time, name, instructor·location, capacity)
   * plus a Book action, instead of booking straight from the bar. */
  private openClassPopover(e: MouseEvent, c: MockClass): void {
    e.stopPropagation();
    const bar = e.currentTarget as HTMLElement;
    const cal = bar.closest(".cal") as HTMLElement | null;
    if (!cal) return;
    const b = bar.getBoundingClientRect();
    const r = cal.getBoundingClientRect();
    const width = 240;
    let left = b.left - r.left;
    if (left + width > r.width) left = Math.max(0, r.width - width - 2);
    this.classPopover = { cls: c, top: b.bottom - r.top + 4, left };
  }

  private closePopover(): void {
    this.classPopover = null;
  }

  /** Select a calendar day → show its class list and auto-scroll to the top of
   * that list (both mobile and desktop). */
  private selectMonthDay(date: Date): void {
    this.selectedMonthDay = date;
    this.classPopover = null;
    void this.updateComplete.then(() => {
      this.renderRoot
        .querySelector(".cal-detail")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  private renderClassPopover(): TemplateResult | typeof nothing {
    const p = this.classPopover;
    if (!p) return nothing;
    const c = p.cls;
    const o = this.opts;
    const external = !!c.externalTickets && !!c.externalTicketUrl;
    const notYetOpen = !external && isNotYetOpen(c, this.nowInstant);
    const closed = this.isPastClass(c) || (!external && isRegistrationClosed(c, this.nowInstant));
    const cap = closed ? null : this.capacityText(c);
    const tzAbbr = this.rowTzAbbrev(c);
    const isWaitlist = !external && c.capacityState === "waitlist";
    const meta = [
      o.hideInstructor || !c.instructor ? "" : `w/ ${c.instructor}`,
      o.hideLocation || !c.location ? "" : c.location,
    ]
      .filter(Boolean)
      .join(" · ");
    return html`
      <div class="cal-pop-backdrop" @click=${() => this.closePopover()}></div>
      <div class="cal-popover" style="top:${p.top}px;left:${p.left}px">
        <button
          class="cal-pop-close"
          aria-label="Close"
          @click=${() => this.closePopover()}
        >
          ✕
        </button>
        <div class="cal-pop-time">
          ${this.displayTime(c)}${
            tzAbbr ? html` (${tzAbbr})` : nothing
          }${c.duration ? html` · ${c.duration}` : nothing}
        </div>
        <div class="cal-pop-name">${c.name} ${renderOfferingBadge(c)}</div>
        ${meta ? html`<div class="cal-pop-meta">${meta}</div>` : nothing}
        ${cap ? html`<div class="cal-pop-cap">${cap.text}</div>` : nothing}
        ${
          c.cancelled
            ? html`<div class="cal-pop-cancel">Cancelled</div>`
            : closed ? html`<div class="cal-pop-cancel">${this.isPastClass(c) ? "Ended" : "Registration closed"}</div>`
            : external
              ? html`<a class="cal-pop-book" href=${c.externalTicketUrl!} target="_self">${o.buttonText ?? "Book class"}</a>`
            : html`${
                  c.offeringKind !== "program" && !notYetOpen && c.buyoutAvailable && typeof c.buyoutPriceMinor === "number"
                    ? html`<button
                        class="cal-pop-book"
                        @click=${() => {
                          this.closePopover();
                          this.onBuyout(c);
                        }}
                      >
                        Buy out this session —
                        ${formatMinorAmount(c.buyoutPriceMinor, c.buyoutCurrency)}
                      </button>`
                    : nothing
                }<button
                  class="cal-pop-book"
                  ?disabled=${notYetOpen || (c.offeringKind === "program" && !c.programBookingUrl)}
                  @click=${() => {
                    this.closePopover();
                    this.onBook(c);
                  }}
                >
                  ${notYetOpen ? "Not yet open" : o.buttonText ?? (c.offeringKind === "program" ? "View program" : isWaitlist ? "Join waitlist" : "Book class")}
                </button>`
        }
      </div>
    `;
  }

  /** Reference format: "Today, Wednesday, May 20" / "Tomorrow, ..." / "Friday, May 22". */
  private formatDayHeader(d: Date): string {
    const long = `${WEEKDAYS_LONG[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
    if (sameDay(d, this.today)) return `Today, ${long}`;
    if (sameDay(d, addDays(this.today, 1))) return `Tomorrow, ${long}`;
    return long;
  }

  // ── N2: timezone handling ──

  /** The visitor's own IANA timezone (e.g. "America/Los_Angeles"). */
  private get viewerTz(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
      return "";
    }
  }

  /** The studio timezone to represent the "location" side of the switcher — the
   * zone of the first loaded class that carries one. (Times are still formatted
   * per-class in each class's OWN zone, so multi-location schedules stay
   * correct; this is only the label/decision zone for the toggle.) */
  private get locationTz(): string {
    return this.schedule.find((c) => c.timezone)?.timezone ?? "";
  }

  /** Show the zone picker whenever there's schedule data with a resolvable zone
   * and real instants to re-project. It's a full IANA zone picker (not just a
   * studio↔viewer toggle), so it's useful even when the studio zone equals the
   * viewer's — the visitor may still want any other zone. */
  private get canSwitchTz(): boolean {
    return (
      (!!this.locationTz || !!this.viewerTz) &&
      this.schedule.some((c) => !!c.startIso)
    );
  }

  /** The zone the picker currently reflects: the visitor's explicit pick, else
   * the studio/location default, else the viewer's own zone. Drives which
   * option is preselected in the dropdown. */
  private get effectiveTz(): string {
    return this.selectedTz || this.locationTz || this.viewerTz;
  }

  /** Short zone label for a given IANA zone (e.g. "EDT", "PST"). */
  private tzAbbrev(tz: string): string {
    return tzAbbrev(tz);
  }

  /** A class's start time formatted for the currently selected zone. The picked
   * zone (`selectedTz`) drives every class time; with nothing picked each class
   * renders in its OWN studio zone (so multi-location schedules stay correct),
   * and a zone-less class falls back to the viewer's local zone. Falls back to
   * the precomputed string when there's no instant to re-project. */
  private displayTime(c: MockClass): string {
    return formatZonedTime(c.startIso, this.selectedTz || c.timezone, c.time);
  }

  /** The zone a given row's time is ACTUALLY rendered in. Mirrors
   * {@link displayTime}'s precedence exactly — visitor's pick, else the class's
   * own studio zone, else the viewer's — so the abbrev can never disagree with
   * the time printed beside it. Note this is per-class, not the single
   * 'effectiveTz': with no explicit pick, a multi-location schedule renders
   * each class in its own studio's zone, and one blanket label would be wrong
   * for some of the rows. */
  private rowTz(c: MockClass): string {
    return this.selectedTz || c.timezone || this.viewerTz;
  }

  /** Short zone label for a row, e.g. "EDT". Empty when it can't be resolved
   * (no zone data at all), in which case callers simply omit it. */
  private rowTzAbbrev(c: MockClass): string {
    const tz = this.rowTz(c);
    const abbr = this.tzAbbrev(tz);
    // tzAbbrev returns the IANA name back when Intl can't produce a short
    // label; a raw "America/New_York" in a 108px column is worse than nothing.
    return abbr && abbr !== tz ? abbr : "";
  }

  /** The zone options for the picker: every zone the engine knows, U.S. first
   * (see `timezoneGroups`), with the studio's zone and the visitor's own zone
   * pinned above them. Pinning also guarantees an option matching the picker's
   * value on an engine too old to enumerate zones. */
  private tzOptions(): TimezoneGroup[] {
    return timezoneGroups([
      { tz: this.locationTz, label: "Studio time" },
      { tz: this.viewerTz, label: "Your time zone" },
    ]);
  }

  /** N2: the time-zone picker rendered alongside the filter pills. A <select>
   * of every zone the engine knows, U.S. zones first and the studio's/visitor's
   * pinned above them; the pick drives every class time (see displayTime).
   * Defaults to (preselects) the studio/location zone via `effectiveTz`. */
  private renderTzSwitch(): TemplateResult | typeof nothing {
    if (!this.canSwitchTz) return nothing;
    const current = this.effectiveTz;
    const options = this.tzOptions();
    return html`
      <div class="tz-pill">
        <span class="tz-ico" aria-hidden="true">${ICON_CLOCK}</span>
        <select
          aria-label="Time zone"
          .value=${current}
          @click=${(e: Event) => e.stopPropagation()}
          @change=${(e: Event) =>
            (this.selectedTz = (e.target as HTMLSelectElement).value)}
        >
          ${options.map(
            (g) =>
              html`<optgroup label=${g.label}>
                ${g.zones.map(
                  (o) => html`<option value=${o.tz}>${o.label}</option>`,
                )}
              </optgroup>`,
          )}
        </select>
        <span class="chev" aria-hidden="true">${ICON_CARET}</span>
      </div>
    `;
  }

  /** Expand/collapse a row's description under its title chevron. Live rows
   * fetch the description on first expand (see fetchEventDescription in
   * live.ts) — the bulk schedule load only carries a `hasDescription` flag,
   * not the text itself. Mock rows already have `description` inlined. */
  private toggleRowExpanded(c: MockClass): void {
    if (!c.description && !c.hasDescription) return;
    const next = new Set(this.expandedRowIds);
    if (next.has(c.id)) {
      next.delete(c.id);
    } else {
      next.add(c.id);
      if (
        !c.description &&
        c.hasDescription &&
        c.eventSlug &&
        !(c.id in this.descriptionCache) &&
        !this.descriptionLoadingIds.has(c.id)
      ) {
        this.loadDescription(c.id, c.eventSlug);
      }
    }
    this.expandedRowIds = next;
  }

  /** SCH-08: expand/collapse a row's instructor bio under the instructor name.
   * Unlike the description, the sanitized bio HTML is already inlined on the
   * class (adapter maps events-filter-data `biography`), so this only toggles
   * expand state — no lazy fetch. Gated on `hasInstructorBio`/`instructorBio`
   * so rows without a bio stay non-interactive. */
  private toggleBioExpanded(c: MockClass): void {
    if (!c.hasInstructorBio || !c.instructorBio) return;
    const next = new Set(this.expandedBioIds);
    if (next.has(c.id)) {
      next.delete(c.id);
    } else {
      next.add(c.id);
    }
    this.expandedBioIds = next;
  }

  private async loadDescription(
    classId: string,
    eventSlug: string,
  ): Promise<void> {
    this.descriptionLoadingIds = new Set(this.descriptionLoadingIds).add(
      classId,
    );
    try {
      const text = await fetchEventDescription(
        this.config?.orgId ?? 0,
        eventSlug,
      );
      this.descriptionCache = { ...this.descriptionCache, [classId]: text };
    } catch {
      this.descriptionCache = { ...this.descriptionCache, [classId]: "" };
    } finally {
      const stillLoading = new Set(this.descriptionLoadingIds);
      stillLoading.delete(classId);
      this.descriptionLoadingIds = stillLoading;
    }
  }

  private onBook(c: MockClass): void {
    const now = new Date();
    if (
      c.cancelled ||
      isPastOccurrence(c, now) ||
      isRegistrationClosed(c, now) ||
      isNotYetOpen(c, now)
    ) {
      this.nowInstant = now;
      return;
    }
    if (c.offeringKind === "program") {
      if (!c.programBookingUrl) return;
      // Preserve the host/customer dashboard while opening the canonical combined flow.
      const event = new CustomEvent("zipper-program-book", {
        detail: {
          classId: c.id,
          eventGroupingId: c.eventGroupingId,
          url: c.programBookingUrl,
        },
        bubbles: true,
        composed: true,
        cancelable: true,
      });
      if (this.dispatchEvent(event))
        window.open(c.programBookingUrl, "_blank", "noopener,noreferrer");
      return;
    }
    // Open the multi-spot booking checkout inline, and emit an event so host
    // pages / the admin preview can observe the interaction.
    this.bookingAction =
      c.capacityState === "waitlist"
        ? CommerceRequestedAction.JoinWaitlist
        : CommerceRequestedAction.Book;
    this.bookingClass = c;
    // The schedule can be much taller than the checkout. Without resetting
    // its scrolling ancestors after the Lit swap, the host page preserves its
    // old Y position and drops the customer into payment/order-summary content
    // near the bottom of checkout. Wait until checkout exists, then bring the
    // component's own top into view for both window and nested host scrollers.
    void this.updateComplete.then(() => {
      this.scrollTop = 0;
      scrollToStart(this);
    });
    this.dispatchEvent(
      new CustomEvent("zipper-book", {
        detail: { classId: c.id, name: c.name },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onBuyout(c: MockClass): void {
    const now = new Date();
    if (
      c.cancelled ||
      isPastOccurrence(c, now) ||
      isRegistrationClosed(c, now) ||
      isNotYetOpen(c, now)
    ) {
      this.nowInstant = now;
      return;
    }
    if (c.offeringKind === "program") {
      this.onBook(c);
      return;
    }
    this.bookingAction = CommerceRequestedAction.Buyout;
    this.bookingClass = c;
    this.dispatchEvent(
      new CustomEvent("zipper-buyout", {
        detail: { classId: c.id, name: c.name },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "zipper-schedule": ZipperSchedule;
  }
}
